import * as CIActions from 'ApplicationEntry/ducks/ProductsRiders/criticalIllness';
import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import {CaseDetails} from 'ApplicationEntry/models/CaseDetails';
import {isValidPolicyNumberPaperElectronic} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {matchCriticalIllnessByType} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/CriticalIllness';
import * as CI from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/OrdinaryCriticalIllness';
import {
  isLoaded as isLoadedSmokerCodes,
  isLoading as isLoadingSmokerCodes,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/SmokerCodes';
import {ProductRiderGroups, ProductsRidersPolicyType} from 'ApplicationEntry/models/ProductsRiders/Families';
import {PlanType} from 'ApplicationEntry/models/ProductsRiders/PlanTypes';
import {isNonEmptyString} from 'Core/models/EmptyString';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import * as ErrorMessages from '../../ErrorMessages';
import './../ProductRiders.sass';

import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import VariableCodeRiderInformation from '../VariableCodeRiderInformation';
import PlanSelect from '../PlanSelect';

type CriticalIllnessProps = {
  caseDetails: CaseDetails;
  criticalIllness: CI.ValidOrdinaryCriticalIllness | CI.InvalidOrdCriticalIllness;
};

const getApplicationType = matchCriticalIllnessByType({
  Group: () => ApplicationType.Group,
  Ordinary: () => ApplicationType.Ordinary,
  NewYork: () => ApplicationType.NewYork,
});

const CriticalIllness = (props: CriticalIllnessProps) => {
  const dispatch = useDispatch();
  const {
    planType,
    productType,
    insuredSC,
    calculatedPremium,
    riderInformation,
    cicr1Rider,
    spouseSC,
    benefitAmount,
    cwa,
    policyNumber,
    replacement,
    section125,
    productList,
    smokerCodes,
  } = props.criticalIllness;

  const [planTypeBlurred, setBlurPlanType] = useState(false);
  const [policyNumberBlurred, setPolicyNumberBlurred] = useState(false);
  const showPlanTypeError = planTypeBlurred && planType.key === 'Empty';
  const [planTypeLabel, setPlanTypeLabel] = useState<HTMLLabelElement | null>();
  const [smokerLabel, setSmokerLabel] = useState<HTMLLabelElement | null>();

  const showPolicyNumberError = policyNumberBlurred && !isValidPolicyNumberPaperElectronic(policyNumber) && replacement;

  useEffect(() => {
    if (isNonEmptyString(productType)) {
      dispatch(
        CIActions.getRiderInformation(productType.value, ProductRiderGroups[ProductsRidersPolicyType.CriticalIllness])
      );
    }
  }, [dispatch, productType]);

  useEffect(() => {
    dispatch(CIActions.getCISmokerCodes());
  }, [dispatch]);

  const getHeader = (header1: string, header2: string) => {
    return (
      <>
        <div className="criticalIllness-section__sub-title-container">
          <Typography variant="subtitle1" className="criticalIllness-section__sub-title-container-title">
            {header1}
          </Typography>
        </div>
        {header2 !== '' ? (
          <div className="criticalIllness-section__sub-title-container">
            <Typography variant="subtitle1" className="criticalIllness-section__sub-title-container-title">
              {header2}
            </Typography>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div className="product-riders-section__policy-type" id="critical-illness-section">
      <form noValidate autoComplete="off" className="critical-illness-section__form">
        <div className="product-riders-section__policy-type__form__row">
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <PlanSelect
                productType={productType}
                productList={productList}
                applicationType={getApplicationType(props.criticalIllness)}
                family={ProductRiderGroups[ProductsRidersPolicyType.CriticalIllness]}
                friendlyName="ord_ci"
                changePlanAction={CIActions.changeProductType}
                getPlansAction={CIActions.getCIProducts}
              />
            </Grid>
          </Grid>

          {productType.key === 'NonEmpty' ? (
            <>
              <Grid container spacing={4}>
                <Grid item xs={12} md={3}>
                  <FormControl
                    error={showPlanTypeError}
                    variant="outlined"
                    className="product-riders-section__policy-type__form__row--field"
                  >
                    <InputLabel ref={setPlanTypeLabel} id="critical-illness-plantype-label">
                      Plan Type
                    </InputLabel>
                    <Select
                      labelId="critical-illness-label-plantype"
                      labelWidth={planTypeLabel?.clientWidth}
                      id="critical-illness-plantype"
                      value={planType.key === 'Empty' ? '' : planType.value}
                      onBlur={() => setBlurPlanType(true)}
                      onChange={e => {
                        dispatch(CIActions.changePlanType(e.target.value as string));
                        if (e.target.value !== 'FAMILY') {
                          dispatch(CIActions.changeSpouseSC(''));
                        }
                      }}
                      displayEmpty
                    >
                      {PlanType.map(plan => (
                        <MenuItem value={plan.value} key={plan.value}>
                          {plan.key}
                        </MenuItem>
                      ))}
                    </Select>
                    {showPlanTypeError && <FormHelperText>{ErrorMessages.REQUIRED_MESSAGE}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                    <InputLabel ref={setSmokerLabel} id="critical-illness-insuredsc-label">
                      Insured Smoking Code
                    </InputLabel>
                    <Select
                      labelId="critical-illness-label-insuredsc"
                      labelWidth={smokerLabel?.clientWidth}
                      id="critical-illness-insuredsc"
                      value={insuredSC.key === 'Empty' ? '' : insuredSC.value}
                      onChange={e => {
                        dispatch(CIActions.changeInsuredSC(e.target.value as string));
                      }}
                      displayEmpty
                    >
                      {isLoadedSmokerCodes(smokerCodes)
                        ? smokerCodes.Data.map(code => (
                            <MenuItem value={code} key={code + 'insured'}>
                              {code}
                            </MenuItem>
                          ))
                        : null}
                    </Select>
                    {isLoadingSmokerCodes(smokerCodes) ? (
                      <CircularProgress className="product-riders-section__policy-type__loading-icon" />
                    ) : null}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl
                    variant="outlined"
                    className="product-riders-section__policy-type__form__row--field"
                    disabled={true}
                  >
                    <InputLabel ref={setSmokerLabel} id="critical-illness-spousesc-label">
                      Spouse Smoking Code
                    </InputLabel>
                    <Select
                      labelId="critical-illness-label-spousesc"
                      labelWidth={smokerLabel?.clientWidth}
                      id="critical-illness-spouse-sc"
                      value={spouseSC.key === 'Empty' ? '' : spouseSC.value}
                      onChange={e => dispatch(CIActions.changeSpouseSC(e.target.value as string))}
                      displayEmpty
                      disabled={planType.value === 'FAMILY' ? false : true}
                    >
                      {isLoadedSmokerCodes(smokerCodes)
                        ? smokerCodes.Data.map(code => (
                            <MenuItem value={code} key={code + 'spouse'}>
                              {code}
                            </MenuItem>
                          ))
                        : null}
                    </Select>
                    {isLoadingSmokerCodes(smokerCodes) ? (
                      <CircularProgress className="product-riders-section__policy-type__loading-icon" />
                    ) : null}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="checkedB"
                        color="secondary"
                        id="critical-illness-section125"
                        checked={section125}
                        onChange={() => dispatch(CIActions.changeSection125(!section125))}
                      />
                    }
                    label={<Typography>Section 125</Typography>}
                    className="product-riders-section__policy-type__form__row--field"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    id="critical-illness-premium"
                    label="Premium"
                    variant="outlined"
                    placeholder="Enter Premium"
                    margin="normal"
                    fullWidth
                    className="product-riders-section__policy-type__form__row--field"
                    value={calculatedPremium.value}
                    onChange={event => {
                      dispatch(CIActions.changePremium(event.target.value as string));
                      dispatch(CIActions.changeCalculatedPremium(event.target.value as string));
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="critical-illness-cwa"
                    label="CWA"
                    variant="outlined"
                    placeholder="Enter CWA"
                    margin="normal"
                    fullWidth
                    className="product-riders-section__policy-type__form__row--field"
                    value={cwa.value}
                    onChange={e => dispatch(CIActions.changeCWA(e.target.value as string))}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="critical-illness-benefit-amt"
                    label="Benefit Amount"
                    variant="outlined"
                    placeholder="Enter Benefit Amount"
                    margin="normal"
                    fullWidth
                    className="product-riders-section__policy-type__form__row--field"
                    value={isNaN(benefitAmount) ? '' : benefitAmount}
                    onChange={e => dispatch(CIActions.changeBenefitAmount(parseInt(e.target.value, 10)))}
                  />
                </Grid>
              </Grid>

              <VariableCodeRiderInformation
                showConsolidatedRiderSection
                indexCodes
                title="Riders"
                amountLabel="Rider Amount"
                enableSuccessively
                riderInformation={riderInformation}
                changeRiderInformationAction={CIActions.changeRiderInformationData}
                removeRiderAction={CIActions.removeRider}
                identifier="ordinary-critical-illness"
              />

              <Grid container spacing={4}>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="checkedB"
                        color="secondary"
                        id="critical-illness-cicr1Rider"
                        checked={cicr1Rider}
                        onChange={() => dispatch(CIActions.changeCICR1Rider(!cicr1Rider))}
                      />
                    }
                    label={<Typography>CICR1</Typography>}
                    className="product-riders-section__policy-type__form__row--field"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={4}>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="checkedB"
                        color="secondary"
                        id="critical-illness-replacement"
                        checked={replacement}
                        onChange={() => {
                          setPolicyNumberBlurred(true);
                          dispatch(CIActions.changeReplacement(!replacement));
                        }}
                      />
                    }
                    label={<Typography className="case-details-section__checkbox-label">Replacement</Typography>}
                    className="product-riders-section__policy-type__form__row--field"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="critical-illness-policy-number"
                    label="Policy Number"
                    variant="outlined"
                    placeholder="Enter Policy Number"
                    margin="normal"
                    fullWidth
                    error={showPolicyNumberError}
                    helperText={showPolicyNumberError ? ErrorMessages.INVALID_POLICY_NUMBER : ''}
                    className="product-riders-section__policy-type__form__row--field"
                    value={policyNumber.key !== 'ValidPolicyNumber' || !replacement ? '' : policyNumber.value.value}
                    onBlur={() => setPolicyNumberBlurred(true)}
                    onChange={e => dispatch(CIActions.changePolicyNumber(e.target.value as string))}
                    disabled={!replacement}
                    inputProps={{
                      maxLength: 10,
                    }}
                  />
                </Grid>
              </Grid>
            </>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default CriticalIllness;
